/* eslint-disable @typescript-eslint/no-explicit-any */
import { ButtonMain } from '@quero-delivery/quero-components-web';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { format, getStartOfDay } from '@querodelivery/qd-date-helper';
import isEmpty from 'lodash/isEmpty';
import type {
  IAddress,
  IDocumentVehicle,
  IMotoboyBank,
  IMotoboyCnh,
} from '@/Interfaces/Data';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import {
  celularLabel,
  cepLabel,
  cnpjLabel,
  cpfLabel,
  objectNotNullV2,
  removeNoNumeric,
  yearDateLabel,
} from '@/Utils/FnUtils';
import { useBanksList } from '@/_features/motoboys/deliveryman-form/hooks/use-banks-list';
import styles from './form.module.scss';
import { MOTOBOY_FORM_SCHEMA } from '../../constants';
import type { DeliverymanFormType } from '../../types';
import { PersonalDataForm } from '../personal-data-form';
import { VehicleDataForm } from '../vehicle-data-form';
import { CnhDataForm } from '../cnh-data-form';
import { BankDataForm } from '../bank-data-form';
import PurpleButton from '@/Components/shared/purple-button/PurpleButton';
import { BoxSendFile } from '../box-send-file';
import { getDocumentType, normalizeCnpj } from '@/_utils/text';

function Form() {
  const navigate = useNavigate();
  const { motoboyId } = useParams<{ motoboyId: string }>();

  const {
    handleCreateMotoboy,
    handleEditMotoboy,
    motoboy,
    createMotoboyState,
    editMotoboyState,
  } = useMotoboy(motoboyId || '');

  const isLoadingRequest =
    createMotoboyState.loading || editMotoboyState.loading;

  const { banksOptions } = useBanksList();

  const defaultValues = {
    nome: motoboy?.nome || '',
    email: motoboy?.email || '',
    senha: '',
    telefone: celularLabel(motoboy?.celular) || '',
    cpf: motoboy?.cpf ? cpfLabel(motoboy.cpf) : '',
    cnpj: motoboy?.cnpj ? cnpjLabel(motoboy.cnpj) : '',
    razaoSocial: motoboy?.razaoSocial || '',
    nomeFantasia: motoboy?.nomeFantasia || '',
    dataNascimento: motoboy?.dataNascimento
      ? format(new Date(motoboy?.dataNascimento), 'yyyy-MM-dd')
      : '',
    cidade: motoboy?.municipioId || '',

    enderecoResidencia: {
      cep: motoboy?.enderecoResidencia
        ? cepLabel(motoboy.enderecoResidencia.cep || '')
        : '',
      bairro: motoboy?.enderecoResidencia?.bairro || '',
      endereco: motoboy?.enderecoResidencia?.logradouro || '',
      numero: motoboy?.enderecoResidencia?.numero || '',
      referencia: motoboy?.enderecoResidencia?.referencia || '',
      complemento: motoboy?.enderecoResidencia?.complemento || '',
    },

    documentoVeiculo: {
      nomeDonoVeiculo: motoboy?.documentoVeiculo?.nome || '',
      cpfDonoVeiculo: motoboy?.documentoVeiculo?.cpf
        ? cpfLabel(motoboy.documentoVeiculo.cpf)
        : '',
      exercicio: motoboy?.documentoVeiculo?.exercicio || '',
      renavam: motoboy?.documentoVeiculo?.renavam || '',
      anoFabricacao: motoboy?.documentoVeiculo?.anoFabricacao
        ? yearDateLabel(motoboy?.documentoVeiculo?.anoFabricacao)
        : '',
      anoModelo: motoboy?.documentoVeiculo?.anoModelo
        ? yearDateLabel(motoboy?.documentoVeiculo?.anoModelo)
        : '',
      modelo: motoboy?.documentoVeiculo?.modelo || '',
      chassi: motoboy?.documentoVeiculo?.chassi || '',
      placa: motoboy?.documentoVeiculo?.placa || '',
    },

    cnh: {
      numeroCNH: motoboy?.cnh?.numero || '',
      categoriaCNH: motoboy?.cnh?.categoria || '',
      dataEmissaoCNH: motoboy?.cnh?.emissao
        ? format(new Date(motoboy?.cnh?.emissao), 'yyyy-MM-dd')
        : '',
      validadeCNH: motoboy?.cnh?.validade
        ? format(new Date(motoboy?.cnh?.validade), 'yyyy-MM-dd')
        : '',
    },

    tipoVeiculo: motoboy?.tipoVeiculo || '',
    tipoPessoa:
      motoboy?.contaBancaria?.cpfCnpjDestinatario?.length === 11
        ? 'fisica'
        : 'juridica',

    contaBancaria: {
      banco: motoboy?.contaBancaria?.bancoNome || '',
      tipoConta: motoboy?.contaBancaria?.tipoConta || '',
      bancoAgencia: motoboy?.contaBancaria?.bancoAgencia || '',
      digitoConta: motoboy?.contaBancaria?.digitoConta || '',
      cpfCnpjDestinatario: motoboy?.contaBancaria?.cpfCnpjDestinatario
        ? motoboy?.contaBancaria?.cpfCnpjDestinatario.length === 11
          ? cpfLabel(motoboy.contaBancaria.cpfCnpjDestinatario)
          : cnpjLabel(motoboy.contaBancaria.cpfCnpjDestinatario)
        : '',
      bancoAgenciaDigito: motoboy?.contaBancaria?.bancoAgenciaDigito || '',
      comprovanteBancarioFile: motoboy?.contaBancaria?.comprovanteUrl || '',
      nomeDestinatario: motoboy?.contaBancaria?.nomeDestinatario || '',
      numeroConta: motoboy?.contaBancaria?.numeroConta || '',
    },
  };

  const methods = useForm<DeliverymanFormType>({
    resolver: yupResolver(MOTOBOY_FORM_SCHEMA),
    context: {
      motoboy,
    },
    defaultValues,
    mode: 'onChange',
  });

  const normalizeCpfOrCnpj = (value: string) => {
    return getDocumentType(value) === 'cpf'
      ? removeNoNumeric(value)
      : normalizeCnpj(value);
  };
  const onSubmit: SubmitHandler<DeliverymanFormType> = async data => {
    // TODO remove any
    const payload: any = {
      municipioId: data.cidade,
      nome: data.nome,
      cpf: removeNoNumeric(data.cpf || ''),
      celular: removeNoNumeric(data.telefone || ''),
      email: data.email,
      senha: data.senha ? data.senha : undefined,
      cnpj: normalizeCnpj(data.cnpj || '') ?? undefined,
      dataNascimento: data.dataNascimento
        ? getStartOfDay(data.dataNascimento).toISOString()
        : undefined,

      tipoVeiculo: data.tipoVeiculo || undefined,

      razaoSocial: data.razaoSocial ?? undefined,
      nomeFantasia: data.nomeFantasia ?? undefined,
    };

    const cnh: Partial<IMotoboyCnh> = {
      numero: data.cnh?.numeroCNH || undefined,
      categoria: data.cnh?.categoriaCNH || undefined,
      emissao: data.cnh?.dataEmissaoCNH
        ? getStartOfDay(data.cnh.dataEmissaoCNH).toISOString()
        : undefined,
      validade: data.cnh?.validadeCNH
        ? getStartOfDay(data.cnh.validadeCNH).toISOString()
        : undefined,
    };

    if (!isEmpty(objectNotNullV2(cnh))) {
      payload.cnh = cnh;
    }

    const documentoVeiculo: Partial<IDocumentVehicle> = {
      nome: data.documentoVeiculo?.nomeDonoVeiculo || undefined,
      cpf: data.documentoVeiculo?.cpfDonoVeiculo
        ? removeNoNumeric(data.documentoVeiculo.cpfDonoVeiculo)
        : undefined,
      exercicio: data.documentoVeiculo?.exercicio || undefined,
      renavam: data.documentoVeiculo?.renavam || undefined,
      anoFabricacao: data.documentoVeiculo?.anoFabricacao || undefined,
      anoModelo: data.documentoVeiculo?.anoModelo || undefined,
      placa: data.documentoVeiculo?.placa || undefined,
      chassi: data.documentoVeiculo?.chassi || undefined,
      modelo: data.documentoVeiculo?.modelo || undefined,
    };

    if (!isEmpty(objectNotNullV2(cnh))) {
      payload.documentoVeiculo = documentoVeiculo;
    }

    if (isEmpty(objectNotNullV2(documentoVeiculo))) {
      payload.documentoVeiculo = undefined;
    }

    const bankName = data.contaBancaria?.banco
      ? banksOptions.find(
          (bank: any) => bank.value === data.contaBancaria?.banco,
        )
      : null;

    const bankAccount: Partial<IMotoboyBank> = {
      bancoNome: data.contaBancaria?.banco || undefined,
      bancoNomeTransfeera: data.contaBancaria?.banco || undefined,
      bancoAgencia: data.contaBancaria?.bancoAgencia || undefined,
      bancoAgenciaDigito: data.contaBancaria?.bancoAgenciaDigito || undefined,
      numeroConta: data.contaBancaria?.numeroConta || undefined,
      digitoConta: data.contaBancaria?.digitoConta || undefined,
      cpfCnpjDestinatario: data.contaBancaria?.cpfCnpjDestinatario
        ? normalizeCpfOrCnpj(data.contaBancaria?.cpfCnpjDestinatario)
        : undefined,
      nomeDestinatario: data.contaBancaria?.nomeDestinatario || undefined,
      comprovanteUrl: data.contaBancaria?.comprovanteBancarioFile || undefined,
      bancoCodigo: bankName ? bankName.key : undefined,
      tipoConta: data.contaBancaria?.tipoConta || undefined,
    };

    if (!isEmpty(objectNotNullV2(bankAccount))) {
      payload.contaBancaria = bankAccount;
    }

    const residenceAddress: Partial<IAddress> = {
      logradouro: data.enderecoResidencia?.endereco || undefined,
      numero: data.enderecoResidencia?.numero || undefined,
      bairro: data.enderecoResidencia?.bairro || undefined,
      cep: data.enderecoResidencia?.cep
        ? removeNoNumeric(data.enderecoResidencia.cep)
        : undefined,
      complemento: data.enderecoResidencia?.complemento || undefined,
      referencia: data.enderecoResidencia?.referencia || undefined,
    };

    if (!isEmpty(objectNotNullV2(residenceAddress))) {
      residenceAddress.municipioId = data.cidade;
      payload.enderecoResidencia = residenceAddress;
    }

    if (motoboy && motoboy._id) {
      const edited = await handleEditMotoboy(payload);

      if (edited) {
        toast.success('Entregador Editado!');
        navigate(`/entregadores/${motoboy._id}`);
      }

      return;
    }

    const created = await handleCreateMotoboy(payload);

    if (created) {
      toast.success('Entregador Criado!');
      navigate(`/entregadores`);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={styles.container}
      >
        <PersonalDataForm />
        <VehicleDataForm />
        <CnhDataForm />
        <BankDataForm />
        <BoxSendFile />

        <div className={styles.actionButtons}>
          <ButtonMain
            secondary
            textFirst="Cancelar"
            className={styles.btnCancelar}
            width="100%"
            disabled={isLoadingRequest}
            type="button"
            onClick={() =>
              motoboyId
                ? navigate(`/entregadores/${motoboyId}`)
                : navigate('/entregadores')
            }
          />
          <PurpleButton
            width="100%"
            className={styles.btnConfirmar}
            textFirst="Salvar"
            loading={isLoadingRequest}
            disabled={isLoadingRequest}
            type="submit"
          />
        </div>
      </form>
    </FormProvider>
  );
}

export { Form };
